import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { t } from 'i18next';
import _ from 'lodash';
import { sqScalingTemplateStore } from '@/core/core.stores';
import { validateFormula } from '@/formula/formula.utilities';
import {
  getExistingTableDefinition,
  setColumnIdToBeUsedAsTemplateInput,
  setDoTableReload,
  setIsApplyingTemplate,
  setSelectedColumns,
  setTemplateInputToSelect,
} from '@/tableDefinitionEditor/tableDefinition.actions';
import { CONTROL_PANE_DISPLAY, EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  setColumnToEditId,
  setControlPane,
  setShowColumnProperties,
} from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { addOrUpdateColumn } from '@/tableDefinitionEditor/tableGridColumns.utilities';
import { invalidateTableTemplate } from '@/tableDefinitionEditor/tableTemplate.queries';
import { normalizeId } from '@/tableDefinitionEditor/templateTableApplication.utilities';
import { closeInvestigationTool } from '@/toolSelection/investigate.actions';
import { ModelColumnHeader } from '@/tools/models/ModelColumnHeader.atom';
import { updateInputFeature } from '@/tools/models/editTemplate.utilities';
import {
  getTemplateInputFeatures,
  resetTemplate,
  setTemplate,
  setTemplateOutput,
} from '@/tools/models/template.actions';
import { fetchItemsForMetadata } from '@/tools/models/template.api';
import {
  TEMPLATE_TYPES,
  type TemplateDerivedFeature,
  type TemplateOutputSelectValue,
} from '@/tools/models/template.types';
import { getTemplateInstanceIdFromProps } from '@/tools/models/templateInstance.utilities';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { addTrendItem } from '@/trendData/trend.actions';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import { clearRetiredTemplatesProps, clearTemplateProps, getTemplateProps } from '@/trendData/trendTemplate.actions';
import { getDependencies } from '@/utilities/formula.utilities';
import { isForbidden } from '@/utilities/http.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { getNextName } from '@/utilities/utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import { OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import {
  type TemplateRuleInputV1String,
  TypeEnum as TemplateRuleInputV1StringTypeEnum,
} from '@seeqdev/api/model/TemplateRuleInputV1String';
import type { QueryClient } from '@tanstack/react-query';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { FormulaEditorParam } from '@/formula/FormulaParametersTable.molecule';
import type { ColumnDefinition, EditorType, ModelToSelect } from '@/tableDefinitionEditor/tableDefinition.types';
import type { FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.atom';
import type { TemplateInput } from '@/tools/models/template.store';
import type {
  CollectedInputsAndParents,
  DerivedFeatureWithColumnDefinition,
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  OutputDependencyItem,
  OutputItemMetadataFields,
  SelectableOutput,
  TemplateType,
  TemplateVersionMetadata,
} from '@/tools/models/template.types';
import type { ApplicationVersionInfo } from '@/tools/models/useTableModelVersionMetadata.hook';
import type { CurrentUser } from '@/workbench/workbench.store';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { InputFeatureV1 } from '@seeqdev/api/model/InputFeatureV1';
import type { InstanceOutputMappingV1 } from '@seeqdev/api/model/InstanceOutputMappingV1';
import type { InstanceOutputV1 } from '@seeqdev/api/model/InstanceOutputV1';
import type { ItemOutputV1 } from '@seeqdev/api/model/ItemOutputV1';
import type { ReferencedOutputItemV1 } from '@seeqdev/api/model/ReferencedOutputItemV1';
import type { TemplateApplicationMappingV1 } from '@seeqdev/api/model/TemplateApplicationMappingV1';
import type { TemplateDerivedColumnNamesInputV1 } from '@seeqdev/api/model/TemplateDerivedColumnNamesInputV1';
import type { TemplateInitialApplicationInputV1 } from '@seeqdev/api/model/TemplateInitialApplicationInputV1';
import type { TemplateReapplicationInputV1 } from '@seeqdev/api/model/TemplateReapplicationInputV1';
import type { TemplateRuleInputV1Object } from '@seeqdev/api/model/TemplateRuleInputV1Object';
import type { TemplateTableApplicationsOutputListV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputListV1';
import type { TemplateTableApplicationsOutputV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/models';

export interface ItemOutputWithLaneAndAxis extends ItemOutputV1 {
  lane?: number;
  axisAlign?: string;
}

export const isFeatureTypeDerivedFeatureWithColumnDefinition = (
  feature: unknown,
): feature is DerivedFeatureWithColumnDefinition & { columnDefinition: Pick<ColumnDefinition, 'id'> } => {
  if (!feature || typeof feature !== 'object') {
    return false;
  }
  if (!('columnDefinition' in feature)) {
    return false;
  }
  const { columnDefinition } = feature;
  return (
    !!columnDefinition &&
    typeof columnDefinition === 'object' &&
    'id' in columnDefinition &&
    typeof columnDefinition.id === 'string'
  );
};

/**
 * Merges version/draft metadata into template items. Pure function --
 * takes a list of templates and a metadata map, returns templates with
 * `versionMetadata` attached where available.
 */
export const mergeVersionMetadata = <T extends { id: string }>(
  models: T[],
  metadataMap: Map<string, TemplateVersionMetadata>,
): T[] =>
  models.map((m) => {
    const metadata = metadataMap.get(m.id);
    return metadata ? { ...m, versionMetadata: metadata } : m;
  });

/**
 * Filters templates based on ownership determined by versionMetadata.
 * Only includes templates where the current user is (or is not) the original creator.
 * TODO: CRAB-60044 Replace with check for owner property after switching to use templates search endpoint
 *
 * @param templates - Array of templates to filter
 * @param currentUser - The current user
 * @param includeOwned - If true, returns templates owned by current user; if false, returns templates NOT owned by current user
 * @returns Filtered array of templates
 */
export const filterTemplatesByOwnership = <T extends { id: string; versionMetadata?: TemplateVersionMetadata }>(
  templates: T[],
  currentUser: CurrentUser | undefined,
  includeOwned: boolean,
): T[] => {
  if (currentUser?.name === undefined || currentUser?.isAdmin === undefined) {
    return templates;
  }

  return templates.filter((template) => {
    const versionMetadata = template.versionMetadata;
    if (!versionMetadata?.versions || versionMetadata.versions.length === 0) {
      // No version history - can't determine ownership, include it
      return true;
    }
    // versions are sorted newest first, so the last one is the original creator
    const firstVersion = versionMetadata.versions[versionMetadata.versions.length - 1];
    const isOwner = firstVersion.creatorName === currentUser.name;
    return includeOwned ? isOwner || currentUser.isAdmin : !isOwner;
  });
};

type ModelLibraryTemplate = {
  id: string;
  versionMetadata?: TemplateVersionMetadata;
  datasource?: { id?: string };
};

export type ModelLibraryTemplatePartition<T extends ModelLibraryTemplate> = {
  myTemplates: T[];
  sharedTemplatesNotOwned: T[];
  industryTemplates: T[];
  /** User-datasource shared templates not owned by the current user, before removing owned duplicates. */
  sharedTemplatesForTotalCount: T[];
};

/** Partitions workbook and shared templates into My / Shared / Industry lists for Model Library display. */
export const partitionModelLibraryTemplates = <T extends ModelLibraryTemplate>(
  templates: T[],
  sharedTemplates: T[],
  currentUser: CurrentUser | undefined,
): ModelLibraryTemplatePartition<T> => {
  const myTemplates = filterTemplatesByOwnership(templates, currentUser, true);
  const myTemplateIds = new Set(myTemplates.map((model) => model.id));

  const sharedUserDatasourceTemplates = sharedTemplates.filter(
    (template) => template.datasource?.id === SeeqNames.LocalDatasources.Templates.User.DatasourceId,
  );
  const sharedTemplatesForTotalCount = filterTemplatesByOwnership(sharedUserDatasourceTemplates, currentUser, false);
  const sharedTemplatesNotOwned = sharedTemplatesForTotalCount.filter((model) => !myTemplateIds.has(model.id));

  const industryTemplates = sharedTemplates.filter(
    (template) => template.datasource?.id === SeeqNames.LocalDatasources.Templates.Industry.DatasourceId,
  );

  return {
    myTemplates,
    sharedTemplatesNotOwned,
    industryTemplates,
    sharedTemplatesForTotalCount,
  };
};

// Match a mapping to a template feature, preferring canonicalId (stable across
// template versions) and falling back to featureId for same-version edits and
// pre-CRAB-56672 rows that have no canonical id persisted.
const findMappingForFeature = (
  mappings: TemplateApplicationMappingV1[],
  feature: { id?: string; canonicalId?: string },
) =>
  mappings.find(
    (m) =>
      (feature.canonicalId !== undefined && m.featureCanonicalId === feature.canonicalId) || m.featureId === feature.id,
  );

/** Fetches a template by ID and sets it in the appropriate template store (Scaling or Tool) */
export const fetchTemplate = async (
  id: string,
  templateType: TemplateType,
  saveState = true,
  isEditing = false,
  mappings: TemplateApplicationMappingV1[] | undefined = undefined,
) => {
  try {
    const { data: template } = await sqTemplatesApi.getTemplate({ templateId: id });
    if (template?.inputFeatures) {
      template.inputFeatures = template.inputFeatures.map((input: InputFeatureV1) => {
        const updatedInput = { ...input } as InputFeatureV1 & {
          isCollapsed?: boolean;
          columnDefinition?: ColumnDefinition;
        };
        if (isEditing) {
          updatedInput.isCollapsed = true;
        }
        if (mappings) {
          const mapping = findMappingForFeature(mappings, input);
          if (mapping && mapping.columnId) {
            updatedInput.columnDefinition = { id: mapping.columnId } as unknown as ColumnDefinition;
          }
        }
        return updatedInput;
      });
    }
    if (template?.derivedFeatures) {
      template.derivedFeatures = template.derivedFeatures.map((derivedFeature: DerivedFeatureV1) => {
        const updatedDerivedFeature: DerivedFeatureWithColumnDefinition = { ...derivedFeature };
        if (mappings) {
          const mapping = findMappingForFeature(mappings, derivedFeature);
          if (mapping && mapping.columnId) {
            updatedDerivedFeature.columnDefinition = { id: mapping.columnId };
          }
        }
        return updatedDerivedFeature;
      });
    }
    if (saveState) {
      setTemplateOutput(templateType, template);
    }
    return template;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/** Builds a camelCase Formula identifier from a Feature name, splitting on every non-alphanumeric char.
 * Non-ASCII letters and underscores are dropped, so a name that yields nothing falls back to `fallback` */
export function featureNameToIdentifier(featureName: string, fallback: string): string {
  const words = featureName.split(/[^a-zA-Z0-9]+/).filter((word) => word.length > 0);
  if (words.length === 0) return fallback;
  const identifier = words
    .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1).toLowerCase()))
    .join('');
  return /^[0-9]/.test(identifier) ? `_${identifier}` : identifier;
}

export function makeUniqueIdentifier(base: string, usedIdentifiers: Set<string>): string {
  // The Formula language binds variables case-insensitively, so compare lowercased to avoid
  // emitting identifiers the backend would reject as duplicates (e.g. `Temp` alongside `temp`).
  const usedLowercase = new Set([...usedIdentifiers].map((name) => name.toLowerCase()));
  // Separate the counter with `_` after a digit so `valueSearch1` dedupes to `valueSearch1_2`, not `valueSearch12`.
  const separator = /[0-9]$/.test(base) ? '_' : '';
  let identifier = base;
  for (let suffix = 2; usedLowercase.has(identifier.toLowerCase()); suffix++) {
    identifier = `${base}${separator}${suffix}`;
  }
  return identifier;
}

export const findUniqueColumnNames = (tableColumnNames: string[], oldColumnNames: string[]) => {
  const newColumnNames: string[] = [];
  const duplicateOutputNames = getDuplicateOutputNames(tableColumnNames, oldColumnNames);
  oldColumnNames.forEach((name) => {
    if (duplicateOutputNames.find((duplicatedName) => duplicatedName === name)) {
      const prefix = `${name}`;
      newColumnNames.push(getNextName([...tableColumnNames, ...newColumnNames], prefix, true, true));
    } else {
      newColumnNames.push(name);
    }
  });

  return newColumnNames;
};

/**
 * Resolves duplicate column names among derived features, respecting priority:
 * 1. Existing table column names (never changed, always take precedence)
 * 2. Output features keep their name over intermediates
 * 3. Intermediate features are renamed if they conflict
 * Within the same priority level, first occurrence keeps the name.
 *
 * @param tableColumnNames - Existing column names in the table
 * @param derivedFeatures - Derived features from the template with name and isOutput properties
 * @returns Array of unique column names in the same order as derivedFeatures
 */
export const findUniqueColumnNamesWithPriority = (
  tableColumnNames: string[],
  derivedFeatures: Pick<ReferencedOutputItemV1, 'name' | 'isOutput'>[],
): string[] => {
  const outputIndices = derivedFeatures
    .map((f, i) => ({ feature: f, originalIndex: i }))
    .filter(({ feature }) => feature.isOutput);
  const intermediateIndices = derivedFeatures
    .map((f, i) => ({ feature: f, originalIndex: i }))
    .filter(({ feature }) => !feature.isOutput);

  const processingOrder = [...outputIndices, ...intermediateIndices];
  const takenNames = [...tableColumnNames];
  const result: string[] = new Array(derivedFeatures.length);

  for (const { feature, originalIndex } of processingOrder) {
    if (takenNames.includes(feature.name)) {
      const uniqueName = getNextName(takenNames, feature.name, true, true);
      result[originalIndex] = uniqueName;
      takenNames.push(uniqueName);
    } else {
      result[originalIndex] = feature.name;
      takenNames.push(feature.name);
    }
  }

  return result;
};

export const getDerivedFeatureId = (feature: DerivedFeatureV1 | NewlyAddedDerivedFeature): string | undefined =>
  'referenceId' in feature ? feature.referenceId : feature.id;

export const updateDerivedFeature = (
  patch: Partial<TemplateDerivedFeature>,
  index: number,
  existingDerivedFeatures: TemplateDerivedFeature[],
  templateType: TemplateType,
) => {
  const updatedFeature: TemplateDerivedFeature = {
    ...existingDerivedFeatures[index],
    ...patch,
  };
  const updatedExistingDerivedFeatures = existingDerivedFeatures.map((feature, i) =>
    i === index ? updatedFeature : feature,
  );
  setTemplate(templateType, { existingDerivedFeatures: updatedExistingDerivedFeatures });
};

export const hasCollapsedInputs = (inputs: TemplateInput[] | NewlyAddedInputFeature[]): boolean =>
  inputs.some((i) => !i.isCollapsed);

export const hasExpandedInputs = (inputs: TemplateInput[] | NewlyAddedInputFeature[]): boolean =>
  inputs.some((i) => i.isCollapsed);

const updateInputFeaturesCollapse = (inputs: TemplateInput[], collapsed: boolean, templateType: TemplateType) => {
  const updated = inputs.map((i) => ({ ...i, isCollapsed: collapsed }));
  setTemplate(templateType, { inputFeatures: updated });
};

const updateExistingInputFeaturesCollapse = (
  inputs: TemplateInput[],
  collapsed: boolean,
  templateType: TemplateType,
) => {
  const updated = inputs.map((i) => ({ ...i, isCollapsed: collapsed }));
  setTemplate(templateType, { existingInputFeatures: updated });
};

const updateNewInputsCollapse = (inputs: NewlyAddedInputFeature[], collapsed: boolean, templateType: TemplateType) => {
  const updated = inputs.map((i) => ({ ...i, isCollapsed: collapsed }));
  setTemplate(templateType, { newInputFeatures: updated });
};

export const expandAllLegacyInputs = (inputs: TemplateInput[], templateType: TemplateType) =>
  updateInputFeaturesCollapse(inputs, false, templateType);

export const collapseAllLegacyInputs = (inputs: TemplateInput[], templateType: TemplateType) =>
  updateInputFeaturesCollapse(inputs, true, templateType);

export const expandAllExistingInputs = (inputs: TemplateInput[], templateType: TemplateType) =>
  updateExistingInputFeaturesCollapse(inputs, false, templateType);

export const collapseAllExistingInputs = (inputs: TemplateInput[], templateType: TemplateType) =>
  updateExistingInputFeaturesCollapse(inputs, true, templateType);

export const expandAllNewInputs = (inputs: NewlyAddedInputFeature[], templateType: TemplateType) =>
  updateNewInputsCollapse(inputs, false, templateType);

export const collapseAllNewInputs = (inputs: NewlyAddedInputFeature[], templateType: TemplateType) =>
  updateNewInputsCollapse(inputs, true, templateType);

/**
 * Parses variable parameter names from variableParameterStrings.
 * Variable parameters are identified by the '$' prefix (e.g., '$a, $b').
 *
 * @param variableParameterArguments - The variableParameterStrings.argument array
 * @returns A Set of variable parameter names (without the '$' prefix)
 */
const parseVariableParameterNames = (variableParameterArguments: unknown): Set<string> => {
  const variableParamNames = new Set<string>();

  if (Array.isArray(variableParameterArguments)) {
    for (const variableParameterString of variableParameterArguments) {
      for (const token of variableParameterString.split(',')) {
        const trimmed = token.trim();
        if (trimmed.startsWith('$')) {
          variableParamNames.add(trimmed.substring(1));
        }
      }
    }
  }

  return variableParamNames;
};

/**
 * Identifies outputs that have variable inputs but none of those variable inputs are mapped.
 *
 * @param existingDerivedFeatures - All derived features (outputs + intermediates) from the template
 * @param existingInputFeatures - All input features with their mapping status
 * @param getMappingStatus - Function that returns true if an input is mapped
 * @returns Array of outputs that have variable inputs but none are mapped
 */
export const getOutputsWithUnmappedVariableInputs = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  existingInputFeatures: TemplateInput[],
  getMappingStatus: (input: TemplateInput) => boolean,
): TemplateDerivedFeature[] => {
  const outputsWithUnmappedInputs: TemplateDerivedFeature[] = [];

  for (const feature of existingDerivedFeatures) {
    if (feature.isOutput) {
      const variableInputIds = new Set<string>();
      for (const rule of feature.rules ?? []) {
        if (rule.formulaCreator) {
          const variableParamNames = parseVariableParameterNames(
            rule.formulaCreator.variableParameterStrings?.argument,
          );

          const parameterNames = rule.formulaCreator.parameters?.argument ?? [];
          if (Array.isArray(parameterNames)) {
            const ruleInputs = rule.inputs ?? [];
            for (let i = 0; i < ruleInputs.length; i++) {
              const input = ruleInputs[i];
              const paramName = parameterNames[i];
              if (input?.inputFeatureId && paramName && variableParamNames.has(paramName)) {
                variableInputIds.add(input.inputFeatureId);
              }
            }
          }
        }
      }

      // If this output has variable inputs, check if any are mapped
      if (variableInputIds.size > 0) {
        const hasAnyMapped = existingInputFeatures.some(
          (input) => input.id && variableInputIds.has(input.id) && getMappingStatus(input),
        );
        if (!hasAnyMapped) {
          outputsWithUnmappedInputs.push(feature);
        }
      }
    }
  }

  return outputsWithUnmappedInputs;
};

/**
 * Determines which input features are used ONLY as variable parameters across all derived feature formulas.
 * These inputs are optional when applying a template because the backend's narrowVariableParameters will
 * exclude them from the formula if unmapped.
 *
 * @param derivedFeatures - All derived features (outputs + intermediates) from the template
 * @returns A Set of input feature IDs that are exclusively used as variable parameters
 */
export const getVariableOnlyInputFeatureIds = (derivedFeatures: TemplateDerivedFeature[]): Set<string> => {
  const variableUsages = new Set<string>();
  const regularUsages = new Set<string>();

  for (const feature of derivedFeatures) {
    for (const rule of feature.rules ?? []) {
      if (rule.formulaCreator) {
        const variableParamNames = parseVariableParameterNames(rule.formulaCreator.variableParameterStrings?.argument);

        const parameterNames = rule.formulaCreator.parameters?.argument ?? [];

        if (Array.isArray(parameterNames)) {
          const ruleInputs = rule.inputs ?? [];

          // Positionally match: ruleInputs[i] <-> parameterNames[i]
          // Iterate over unfiltered inputs to maintain correct positional alignment
          for (let i = 0; i < ruleInputs.length; i++) {
            const input = ruleInputs[i];
            const paramName = parameterNames[i];
            if (input?.inputFeatureId && paramName) {
              if (variableParamNames.has(paramName)) {
                variableUsages.add(input.inputFeatureId);
              } else {
                regularUsages.add(input.inputFeatureId);
              }
            }
          }
        }
      }
    }
  }

  // Variable-only = has variable usage AND no regular usage
  const variableOnly = new Set<string>();
  for (const id of variableUsages) {
    if (!regularUsages.has(id)) {
      variableOnly.add(id);
    }
  }
  return variableOnly;
};

/**
 * Gets the display name for a rule parameter by finding its matching input or derived feature.
 */
const getParameterDisplayName = (
  input: TemplateRuleInputV1Object,
  existingInputFeatures: TemplateInput[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): string => {
  const matchingInputFeature = existingInputFeatures.find((f) => f.id === input.inputFeatureId);
  if (matchingInputFeature) {
    // Existing features always use their feature name
    if (matchingInputFeature.canonicalId) {
      return matchingInputFeature.name;
    }
    // New features prefer selected item name, fall back to feature name
    return matchingInputFeature.selectedItem?.name ?? matchingInputFeature.name ?? input.name;
  }

  const matchingDerivedFeature = existingDerivedFeatures.find((f) => f.id === input.inputFeatureId);
  if (matchingDerivedFeature) {
    return matchingDerivedFeature.name;
  }

  return input.name;
};

/**
 * Extracts formula and parameters from a feature rule for display.
 */
const extractFormulaFromRule = (
  rule: TemplateDerivedFeature['rules'][number],
  existingInputFeatures: TemplateInput[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): { formula: string; parameters: { name: string; identifier: string; item?: { id?: string } }[] } => {
  const formula = String(rule?.formulaCreator?.formula?.argument ?? '');
  const paramArgs = Array.isArray(rule?.formulaCreator?.parameters?.argument)
    ? rule.formulaCreator.parameters.argument
    : [];
  const inputs = Array.isArray(rule.inputs) ? rule.inputs : [];

  const parameters = inputs.map((input: TemplateRuleInputV1Object, idx: number) => {
    const matchingInputFeature = existingInputFeatures.find((f) => f.id === input.inputFeatureId);
    return {
      name: getParameterDisplayName(input, existingInputFeatures, existingDerivedFeatures),
      identifier: paramArgs[idx] ?? '',
      item: matchingInputFeature ? { id: matchingInputFeature.inputItem } : undefined,
    };
  });

  return { formula, parameters };
};

/**
 * Displays existing template features (TemplateDerivedFeature) as options for the FormulaItemTable.
 *
 * @param existingDerivedFeatures - Existing derived features from a template
 * @param isOutput - True to show outputs, false to show intermediates
 * @param existingInputFeatures - Model input features for parameter name resolution
 * @returns Options for the FormulaItemTable
 */
export const displayExistingTemplateFeatures = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  isOutput: boolean,
  existingInputFeatures: TemplateInput[],
): FormulaItemTableOption[] =>
  existingDerivedFeatures
    .filter((feature) => feature.isOutput === isOutput && feature.id !== undefined)
    .map((feature) => ({
      key: feature.id ?? feature.name,
      display: feature.name,
      allFormulas: (feature.rules ?? []).map((rule) =>
        extractFormulaFromRule(rule, existingInputFeatures, existingDerivedFeatures),
      ),
      outputItemType: feature.outputItemType,
      outputItemMetadata: feature.outputItemMetadata,
    }));

/**
 * Converts the features of a template into options to be display in the Formula Item Table
 *
 * @param {DerivedFeatureV1[]} features - The features to convert to options
 * @param {boolean} isOutput - True if the features are outputs, false if intermediates
 * @param {TemplateInput[]} inputFeatures - Model input features
 * @param {ColumnDefinition[]} tableColumns - Optional table columns
 * @param {boolean} useIdForKey - True to use the ID as the option key, false to use the name
 * @return {FormulaItemTableOption[]} The options for the formula item table
 */
export const displayTemplateFeatures = (
  features: DerivedFeatureV1[] | DerivedFeatureWithColumnDefinition[],
  isOutput: boolean,
  inputFeatures: TemplateInput[],
  tableColumns?: ColumnDefinition[],
  useIdForKey: boolean | undefined = false,
): FormulaItemTableOption[] =>
  features
    .filter(
      (feature: DerivedFeatureWithColumnDefinition) =>
        feature.isOutput === isOutput &&
        feature.id !== undefined &&
        (!('displayName' in feature) ||
          'showInIntermediates' in feature ||
          'featureId' in feature ||
          'columnDefinition' in feature),
    )
    .map((f: DerivedFeatureWithColumnDefinition) => {
      const allFormulas = (f.rules ?? []).map((rule) => {
        let formula = '';
        let parameters: { name: string; identifier: string; item?: { id?: string } }[] = [];

        if (
          rule &&
          typeof rule === 'object' &&
          'arguments' in rule &&
          rule.arguments &&
          typeof rule.arguments === 'object' &&
          'formula' in rule.arguments &&
          'parameters' in rule.arguments &&
          'inputs' in rule
        ) {
          formula = (rule.arguments.formula as string) ?? '';

          const paramStr = rule.arguments.parameters as string;
          const paramArray = paramStr ? paramStr.split('|') : [];
          const ruleInputs: string[] = Array.isArray(rule.inputs)
            ? rule.inputs.map((input) => (typeof input === 'string' ? input : ''))
            : [];

          parameters = ruleInputs.map((input: string, idx: number) => ({
            name: tableColumns ? tableColumns.find((col) => col.id === input)?.displayName || input : input,
            identifier: paramArray[idx] ?? '',
            item: undefined,
          }));
        } else if (rule?.formulaCreator?.formula?.argument) {
          formula = String(rule.formulaCreator.formula.argument);

          const paramArgs = Array.isArray(rule.formulaCreator.parameters?.argument)
            ? rule.formulaCreator.parameters.argument
            : [];
          const inputs = Array.isArray(rule.inputs) ? rule.inputs : [];

          parameters = inputs.map((input: TemplateRuleInputV1Object, idx: number) => {
            const matchingInputFeature = inputFeatures
              ? inputFeatures.find((feature) => feature.id === input.inputFeatureId)
              : undefined;
            return {
              name: matchingInputFeature?.name ?? input.name,
              identifier: paramArgs[idx] ?? '',
              item: matchingInputFeature ? { id: matchingInputFeature.inputItem } : undefined,
            };
          });
        }
        return { formula, parameters };
      });

      let displayName = 'columnName' in f ? String(f.columnName) : String(f.name);

      return {
        key: useIdForKey ? (f.id ?? '') : f.name,
        display: displayName,
        allFormulas,
        outputItemType: f.outputItemType,
        outputItemMetadata: f.outputItemMetadata,
      };
    });

/** Validates a formula and its parameters for a one off template application.
 *
 * @param {string} formula - the formula to validate
 * @param {Array<{name: string; identifier: string; item?: {id?: string}}>} parameters - the parameters used in the formula
 */
export const validateApplication = (
  formula: string,
  parameters: { name: string; identifier: string; item?: { id?: string } }[],
) => {
  // If the ID is missing, this is for an intermediate and there is no real item yet
  const missingId = parameters.some((param) => !param.item?.id);
  if (missingId) {
    return { formula, parameters };
  }
  return validateFormula(formula, parameters as unknown as FormulaEditorParam[])
    .then(() => {
      return { formula, parameters };
    })
    .catch((formulaErrors) => {
      return { formula, parameters, formulaErrors };
    });
};

/** Validates all formulas in all options of a Formula Item Table. If any fallback formulas are valid, clears errors
 * for that option. Returns a new array with validated options.
 * @param {FormulaItemTableOption[]} options - the formula item table options to validate
 * @returns {Promise<{ validatedOptions: FormulaItemTableOption[]; hasErrors: boolean }>} Validated options and error flag
 */
export async function validateAllOptionsFormulas(
  options: FormulaItemTableOption[],
): Promise<{ validatedOptions: FormulaItemTableOption[]; hasErrors: boolean }> {
  let formulaErrorsExist = false;

  const validatedOptions = await Promise.all(
    options.map(async (option) => {
      let optionHasErrors = false;
      let validatedFormulas = option.allFormulas;

      if (option.allFormulas && Array.isArray(option.allFormulas)) {
        const newFormulas: typeof option.allFormulas = [];
        let foundValid = false;

        // Process formulas sequentially to short-circuit on first valid fallback
        for (const formula of option.allFormulas) {
          if (foundValid) {
            // Keep remaining formulas unchanged after finding valid one
            newFormulas.push(formula);
          } else {
            try {
              const result = await exposedForTesting.validateApplication(formula.formula, formula.parameters);
              if ('formulaErrors' in result && result.formulaErrors) {
                newFormulas.push({ ...formula, formulaErrors: result.formulaErrors });
                optionHasErrors = true;
              } else {
                // Valid formula found - clear all errors and mark to skip validation of remaining
                newFormulas.push({ ...formula, formulaErrors: undefined });
                optionHasErrors = false;
                foundValid = true;
              }
            } catch (error) {
              optionHasErrors = true;
              errorToast({ httpResponseOrError: error });
              newFormulas.push(formula);
            }
          }
        }

        // If a valid fallback was found, clear all errors
        if (!optionHasErrors && newFormulas.length > 0) {
          validatedFormulas = newFormulas.map((formula) => ({ ...formula, formulaErrors: undefined }));
        } else {
          validatedFormulas = newFormulas;
        }
      }

      if (optionHasErrors) {
        formulaErrorsExist = true;
      }

      return {
        ...option,
        allFormulas: validatedFormulas,
      };
    }),
  );

  return { validatedOptions, hasErrors: formulaErrorsExist };
}

/** Updates a specific input in the template's input features list
 *
 * @param patch - the fields to update in the input
 * @param input - the input to update
 * @param templateType - the type of template being updated (Scaling or Tool)
 */
export const updateInput = (patch: Partial<TemplateInput>, input: TemplateInput, templateType: TemplateType) => {
  const idToUse = input.referenceId ?? input.id;
  if (!idToUse) {
    return;
  }
  // Makes sure we are using the most up-to-date inputs from the store
  const inputFeatures = getTemplateInputFeatures(templateType);

  let updated = false;
  const updatedFeatures = inputFeatures.map((input) => {
    const inputId = input.referenceId ?? input.id;
    if (!updated && inputId === idToUse) {
      updated = true;
      return { ...input, ...patch };
    }
    return input;
  });

  setTemplate(templateType, { inputFeatures: updatedFeatures });
};

export const renameToUniqueDerivedFeatureNames = (columns: ColumnDefinition[], templateType: TemplateType) => {
  const existingDerivedFeatures = _.cloneDeep(sqScalingTemplateStore.existingDerivedFeatures);
  const newColumnNames = findUniqueColumnNamesWithPriority(
    columns.map((column) => column.displayName),
    existingDerivedFeatures,
  );
  newColumnNames.forEach((newName, index) => {
    existingDerivedFeatures[index].name = newName;
  });

  setTemplate(templateType, { existingDerivedFeatures });
};

/** Validates that none of the derived feature names already exist in the scaling table column names.
 * If any do, throws an error listing the duplicates.
 *
 * @param scalingTableColumnNames - the existing column names in the scaling table
 * @param derivedFeatureNames - the names of the derived features in the template
 */
export const validateTemplateOutputNames = (scalingTableColumnNames: string[], derivedFeatureNames: string[]) => {
  const outputNamesInTable = getDuplicateOutputNames(scalingTableColumnNames, derivedFeatureNames);
  if (outputNamesInTable.length) {
    const listOfNames = outputNamesInTable.map((name) => `"${name}"`).join(', ');
    const namesPlural = outputNamesInTable.length > 1 ? 'names' : 'name';
    const colsPlural = outputNamesInTable.length > 1 ? 'columns' : 'column';

    throw new Error(
      `Applying this template would create duplicate column ${namesPlural}: ${listOfNames}. ` +
        `Please rename the conflicting table ${colsPlural} and try again.`,
    );
  }
};

/**
 * Returns a list of duplicate output names that exist in both the scaling table column names and the derived feature names.
 *
 * @param {string[]} scalingTableColumnNames - The existing column names in the scaling table.
 * @param {string[]} derivedFeatureNames - The names of the derived features in the template.
 * @returns {string[]} An array of duplicate output names found in both lists.
 */
export const getDuplicateOutputNames = (scalingTableColumnNames: string[], derivedFeatureNames: string[]) => {
  const outputNamesInTable = Array.from(
    new Set(derivedFeatureNames.filter((name) => scalingTableColumnNames.includes(name))),
  );

  return [...outputNamesInTable];
};

/**
 * Maps a trend-store item to the wire-format `outputItemType` and `outputItemMetadata` carried on a
 * `DerivedFeatureV1`. Mirrors the backend's `FeatureOutputItemMetadata.toMetadataMap` (see
 * `appserver/items/.../Model.kt`):
 *   - Series  -> "Signal"   + `{ [ValueUom]: <uom> }`
 *   - Scalar  -> "Scalar"   + `{ [Uom]: <uom> }`
 *   - Condition -> "Condition" + `{}`
 *
 * A `valueUnitOfMeasure` of `undefined` omits the UOM key entirely (Any-UOM opt-out); an empty
 * string is preserved as a present-but-empty value (dimensionless).
 */
export const toOutputItemMetadata = (item: {
  itemType: ITEM_TYPES;
  valueUnitOfMeasure?: string;
}): OutputItemMetadataFields => {
  const uomEntry = (key: string): Record<string, string> =>
    item.valueUnitOfMeasure === undefined ? {} : { [key]: item.valueUnitOfMeasure };
  switch (item.itemType) {
    case ITEM_TYPES.SERIES:
      return {
        outputItemType: SeeqNames.FormulaType.Signal,
        outputItemMetadata: uomEntry(SeeqNames.Properties.ValueUom),
      };
    case ITEM_TYPES.SCALAR:
      return {
        outputItemType: SeeqNames.FormulaType.Scalar,
        outputItemMetadata: uomEntry(SeeqNames.Properties.Uom),
      };
    case ITEM_TYPES.CONDITION:
      return { outputItemType: SeeqNames.FormulaType.Condition, outputItemMetadata: {} };
    default:
      return {};
  }
};

/** Maps a pending picker output to a `SelectableOutput` ready for save, stamping any
 *  confirm-time-resolved metadata onto the result. */
export const toSelectableOutput = (
  output: TemplateOutputSelectValue,
  metadata?: OutputItemMetadataFields,
): SelectableOutput => ({
  ...output,
  ...metadata,
  isOutput: true,
  rules: [],
});

/** Fetches all items from the details pane of the supported types, decorates them, and returns the calculated
 *  outputs */
export const getAllDetailsPaneOutputs = (): NewlyAddedDerivedFeature[] => {
  const items = getAllItems({
    excludeDataStatus: [ITEM_DATA_STATUS.REDACTED],
    itemTypes: [ITEM_TYPES.CONDITION, ITEM_TYPES.SERIES, ITEM_TYPES.SCALAR],
  }).filter((item) => item.calculationType !== undefined && item.calculationType !== 'periodic-condition');

  const decoratedItems = decorate({ items });
  const outputs = Array.isArray(decoratedItems) ? decoratedItems : [decoratedItems];

  return outputs.map((output) => {
    const { outputItemType, outputItemMetadata } = toOutputItemMetadata(output);
    return {
      id: output.id,
      name: output.name,
      isOutput: true,
      outputItemType,
      outputItemMetadata,
    };
  });
};

/** Filters detail pane outputs to only those not already in the pending list */
export const getNewDetailsPaneOutputs = (pendingOutputs: TemplateOutputSelectValue[]): TemplateOutputSelectValue[] => {
  const calculatedItems: TemplateOutputSelectValue[] = getAllDetailsPaneOutputs().map(({ id, name }) => ({
    id: id!,
    name,
  }));
  const existingIds = new Set(pendingOutputs.map((p) => p.id));
  return calculatedItems.filter((item) => !existingIds.has(item.id));
};

const toTemplateIntermediatesFromItems = (
  items: Array<{
    id: string;
    name: string;
    inputType: string;
  }>,
  metadataMap: Map<string, OutputItemMetadataFields>,
) =>
  items.map((item) => ({
    referenceId: item.id,
    name: item.name,
    isOutput: false,
    selectedItem: {
      id: item.id,
      name: item.name,
    },
    ...(metadataMap.get(item.id) ?? {}),
  }));

/** Collects inputs and their parent IDs from a list of dependencies. */
export function collectInputsAndParents(dependencies: OutputDependencyItem[]): CollectedInputsAndParents {
  const parentIds = new Set<string>();
  const inputsById = new Map<
    string,
    {
      id: string;
      name: string;
      originalName: string;
      inputType: string;
      description: string;
      parent?: string;
    }
  >();

  dependencies.forEach((dep) => {
    if (!dep?.id) return;

    if (!inputsById.has(dep.id)) {
      const lastAncestorName = dep.ancestors?.length ? dep.ancestors[dep.ancestors.length - 1].name : undefined;

      inputsById.set(dep.id, {
        name: dep.name,
        originalName: dep.name,
        id: dep.id,
        inputType: dep.type,
        description: '',
        parent: lastAncestorName,
      });
    }

    dep.parameterOf?.forEach((param) => {
      if (param?.id) {
        parentIds.add(param.id);
      }
    });
  });

  return { inputs: Array.from(inputsById.values()), parentIds };
}

/** Given a set of template outputs, fetches their dependencies and finds all input and intermediate items. */
export const fetchTemplateToolInputsAndIntermediates = async (
  outputs: NewlyAddedDerivedFeature[],
  inputs: NewlyAddedInputFeature[],
) => {
  const outputIds = outputs.map((output) => output.id);
  const dependencyLists = await Promise.all(
    outputIds.map(async (outputId) => {
      const deps = await getDependencies({ id: outputId as string });
      return deps?.dependencies ?? [];
    }),
  );

  const { inputs: updatedInputs, parentIds } = collectInputsAndParents(dependencyLists.flat());

  const validInputs = updatedInputs
    .filter((input) => input?.id && !parentIds.has(input.id))
    .filter((input) => input.inputType !== SeeqNames.Types.CalculatedCondition);

  const newInputs = validInputs
    .filter((validInput) => {
      return (
        !!validInput.id &&
        !inputs.some((input) => input.referenceId === validInput.id) &&
        !outputs.some((output) => output.id === validInput.id) &&
        validInput.inputType !== SeeqNames.Types.CalculatedScalar
      );
    })
    .map((input) => ({
      id: input.id!,
      referenceId: input.id!,
      name: input.name,
      inputType: input.inputType,
      description: input.description,
      originalName: input.originalName,
      parent: input.parent,
      selectedItem: { id: input.id!, name: input.name },
      isCollapsed: false,
    }));

  const existingInputs: NewlyAddedInputFeature[] = inputs.filter(
    (input) =>
      validInputs.some((validInput) => validInput.id === input.referenceId) &&
      !outputs.some((output) => output.id === input.referenceId),
  );

  const intermediates = updatedInputs.filter(
    (input) =>
      input.inputType !== SeeqNames.Types.StoredSignal &&
      !newInputs.some((newInput) => newInput.referenceId === input.id) &&
      !inputs.some((existingInput) => existingInput.referenceId === input.id) &&
      !outputIds.includes(input.id),
  );

  // Fetch metadata for intermediates
  const intermediateIds = intermediates.map((i) => i.id);
  const metadataMap = await fetchItemsForMetadata(intermediateIds);

  const intermediatesAsDerived: NewlyAddedDerivedFeature[] = toTemplateIntermediatesFromItems(
    intermediates,
    metadataMap,
  ).map((intermediate) => ({
    id: intermediate.referenceId!,
    referenceId: intermediate.referenceId!,
    name: intermediate.name,
    isOutput: false,
    rules: [],
    selectedItem: { id: intermediate.referenceId!, name: intermediate.name },
    outputItemType: intermediate.outputItemType,
    outputItemMetadata: intermediate.outputItemMetadata,
  }));

  return {
    newInputFeatures: [...existingInputs, ...newInputs],
    newDerivedFeatures: [...outputs, ...intermediatesAsDerived],
  };
};

export const getInputIcon = (type: string | undefined) => {
  switch (type) {
    case SeeqNames.Types.StoredCondition:
      return "<i class='fc fc-capsule-set sq-icon-color ml3'></i>";
    case SeeqNames.Types.StoredSignal:
      return "<i class='fc fc-series sq-icon-color ml3'></i>";
    case SeeqNames.Types.CalculatedScalar:
      return "<i class='fc fc-scalar sq-icon-color ml3'></i>";
    default:
      return '';
  }
};

export const getValidColumnType = (outputType: OutputTypeEnum | undefined) => {
  switch (outputType) {
    case OutputTypeEnum.ITEMID:
      return ColumnTypeEnum.UUID;
    case OutputTypeEnum.NUMERIC:
      return ColumnTypeEnum.NUMERIC;
    case OutputTypeEnum.TEXT:
      return ColumnTypeEnum.TEXT;
    default:
      return null;
  }
};

export const resetTemplateApplicationAndEdit = (editorType: EditorType) => {
  setIsApplyingTemplate(editorType, false);
  setColumnIdToBeUsedAsTemplateInput(editorType, undefined);
  setTemplateInputToSelect(editorType, undefined);
  setColumnToEditId(editorType, undefined);
  setShowColumnProperties(editorType, false);
  setSelectedColumns(editorType, []);
};

/** Adds a newly created input column to the scaling table and updates the corresponding input feature in the template's
 *  input features list.
 */
export const updateNewlyCreatedTemplateInput = async (
  editorType: EditorType,
  tableDefinitionId: string,
  columnDefinition: ColumnDefinitionInputV1,
  templateInputToSelect: ModelToSelect | undefined,
  existingInputFeatures: TemplateInput[],
) => {
  try {
    const output = await addOrUpdateColumn(editorType, tableDefinitionId, columnDefinition, undefined);
    if (output) {
      const colDefs = output.tableDefinition.columnDefinitions;
      const addedColumn = colDefs[colDefs?.length - 1];
      if (!addedColumn) {
        return;
      }

      setColumnIdToBeUsedAsTemplateInput(editorType, addedColumn.id);

      if (templateInputToSelect) {
        const foundColumn = colDefs.find((column: { id: string }) => column.id === addedColumn.id);
        const inputFeatureToUpdate = existingInputFeatures.find((input) => input.id === templateInputToSelect.id);

        if (foundColumn && inputFeatureToUpdate) {
          updateInputFeature(
            {
              columnDefinition: {
                ...foundColumn,
                displayProperty: SeeqNames.Properties.Name,
              } as ColumnDefinition,
            },
            inputFeatureToUpdate,
            [],
            existingInputFeatures,
            false,
            TEMPLATE_TYPES.SCALING,
          );
        }
      }

      setControlPane(editorType, CONTROL_PANE_DISPLAY.TEMPLATE_CONTROL);
    }
  } catch (err) {
    errorToast({ httpResponseOrError: err });
  }
};

/**
 * Applies a selected template once and creates the derived features as calculated items.
 */
export const applyTemplateOnce = async (modelId: string, input: TemplateInitialApplicationInputV1) => {
  const { data: modelOutput } = await sqTemplatesApi.applyTemplate(input, { templateId: modelId });
  return modelOutput;
};

/** Adds items created from applying a template to the trend. */
export const addTemplateItemsToTrend = async (
  outputs: InstanceOutputMappingV1[],
  lane?: number,
  axisAlign?: string,
) => {
  const validOutputs = outputs.filter(
    (output): output is InstanceOutputMappingV1 & { outputId: string } => !!output.outputId,
  );

  if (validOutputs.length === 0) {
    return;
  }

  const items = await Promise.all(
    validOutputs.map((output) => sqItemsApi.getItemAndAllProperties({ id: output.outputId }).then(({ data }) => data)),
  );

  // Get instanceId once from the first item — all outputs share the same instance
  const instanceId = getTemplateInstanceIdFromProps(items.find((item) => item?.properties)?.properties);

  if (instanceId) {
    // Get template props once, reuse for all
    const props = await getTemplateProps(instanceId);
    await Promise.all(
      items
        .filter(Boolean)
        .map((item) => addTrendItem({ ...item, lane, axisAlign } as ItemOutputWithLaneAndAxis, props)),
    );
  }
};

export const getInstanceById = async (instanceId: string): Promise<InstanceOutputV1> => {
  const { data: instance } = await sqTemplatesApi.getInstance({ instanceId });
  return instance;
};

export const reapplyTemplate = async (modelId: string, instanceId: string, input: TemplateReapplicationInputV1) => {
  const { data: modelOutput } = await sqTemplatesApi.reapplyTemplate(input, { templateId: modelId, instanceId });
  return modelOutput;
};

/** Applies a template once using the specified input features and adds the resulting items to the details pane. */
export const executeSingleTemplateApplication = async (
  modelId: string,
  inputFeatures: TemplateInput[],
  workbookId: string,
  instanceToEditId: string | undefined,
  instanceName: string,
  variableOnlyInputIds: Set<string>,
) => {
  try {
    const inputMappings = createInputMappings({
      inputFeatures,
      variableOnlyInputIds,
      isMapped: (input) => !!input.inputItem,
      instanceName,
      getInputMapping: (input) => ({ itemIdInput: input.inputItem }),
    });

    if (!instanceToEditId) {
      const result = await exposedForTesting.applyTemplateOnce(modelId, {
        inputs: inputMappings,
        sourceId: workbookId,
        datasourceId: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceId,
        datasourceClass: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceClass,
      } as TemplateInitialApplicationInputV1);

      if (!result) {
        return;
      }

      await exposedForTesting.addTemplateItemsToTrend(result.outputs as InstanceOutputMappingV1[]);
      successToast({ messageKey: 'TEMPLATES.APPLY_ONCE_MESSAGE' });
    } else {
      const result = await exposedForTesting.reapplyTemplate(modelId, instanceToEditId, {
        inputs: inputMappings,
        sourceId: workbookId,
        datasourceId: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceId,
        datasourceClass: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceClass,
      } as TemplateReapplicationInputV1);

      await exposedForTesting.addTemplateItemsToTrend(result.outputs as InstanceOutputMappingV1[]);
      successToast({ messageKey: 'TEMPLATES.REAPPLY_MESSAGE' });
    }
    resetTemplate(TEMPLATE_TYPES.TOOL);
    closeInvestigationTool();
  } catch (e) {
    if (isForbidden(e)) {
      errorToast({ messageKey: 'TEMPLATES.REAPPLY_NO_PERMISSION' });
    } else {
      errorToast({ httpResponseOrError: e });
    }
  }
};

/** Retires the instance on the backend and clears out local trend properties related to templates */
export const retireInstance = async (instanceId: string, templateType: TemplateType): Promise<void> => {
  let instance;
  try {
    instance = (await sqTemplatesApi.getInstance({ instanceId })).data;
    await sqTemplatesApi.retireInstance({ instanceId });
  } catch (e) {
    if (isForbidden(e)) {
      errorToast({ messageKey: 'TEMPLATES.RETIRE_NO_PERMISSION' });
    } else {
      errorToast({ httpResponseOrError: e });
    }
    return;
  }
  if (instance.outputs) {
    const itemsPromises = instance.outputs
      .map((output) => {
        if (output.outputId) {
          return sqItemsApi.getItemAndAllProperties({ id: output.outputId });
        }
        return undefined;
      })
      .filter((promise) => !!promise);
    const itemsData = await Promise.all(itemsPromises);
    itemsData.forEach((itemData) => {
      if (itemData) {
        clearTemplateProps(itemData.data.id);
      }
    });
  }
  resetTemplate(templateType);
};

/** Retires a template table instance */
export const retireTableInstance = async (
  applicationId: string,
  templateType: TemplateType,
  shouldDeleteColumns = false,
): Promise<void> => {
  await sqTemplatesApi.retireApplication({ applicationId, deleteColumns: shouldDeleteColumns });
  resetTemplate(templateType);
  await clearRetiredTemplatesProps({ onlyScalingItems: true });
};

export const retireTableInstanceAndResetTable = async (
  applicationId: string | undefined,
  tableDefinitionId: string,
  queryClient: QueryClient,
  templateId: string,
  shouldDeleteColumns?: boolean,
) => {
  const editorType = EDITOR_TYPES.SCALING;
  const templateType = TEMPLATE_TYPES.SCALING;
  if (applicationId) {
    try {
      await exposedForTesting.retireTableInstance(applicationId, templateType, shouldDeleteColumns);
    } catch (e) {
      if (isForbidden(e)) {
        errorToast({ messageKey: 'TEMPLATES.RETIRE_NO_PERMISSION' });
      } else {
        errorToast({ httpResponseOrError: e });
      }
      return;
    }
    await getExistingTableDefinition(tableDefinitionId, editorType);
    await invalidateTableTemplate(queryClient, templateId);

    setDoTableReload(editorType, true);
    setTemplateInputToSelect(editorType, undefined);
    setIsApplyingTemplate(editorType, false);
  }
};

/** Given a list of template applications, fetches all input features and returns a list of their mapped column IDs. */
export const fetchInputFeaturesFromTableApplication = async (
  templateColumns: TemplateTableApplicationsOutputListV1,
) => {
  const applications = templateColumns?.applications ?? [];
  const ids = new Set<string>();
  for (const application of applications) {
    const template = await fetchTemplate(application.templateGuid!, TEMPLATE_TYPES.SCALING, false);
    if (template?.inputFeatures) {
      template.inputFeatures.forEach((feature) => {
        const mapping = application.mappings!.find((mapping) => mapping.featureId === feature.id);
        if (mapping?.columnId) {
          ids.add(mapping.columnId);
        }
      });
    }
  }
  return Array.from(ids);
};

/**
 * Given a list of column definitions and a list of template applications, returns a new list of column definitions
 * where columns that are outputs of a template application are grouped under that template's name.
 *
 * @param agColumnDefs - The list of column definitions to group
 * @param applications - The list of template applications to use for grouping
 * @param inputFeatureColumnIds - The list of column IDs that are input features (these should not be grouped)
 * @param versionByModelGuid - When supplied, attaches the ModelColumnHeader as the column-group's
 *   headerGroupComponent so each template group can render version metadata (e.g. "v{N}").
 * @param editingTemplate - Template container for the application being edited in the template
 * @param editingTemplatePreviewColumnIds - Preview column ids for new outputs selected during template edit
 * @param onUpgradeApplication - When supplied alongside `versionByModelGuid`, the column-group header
 *   renders a clickable upgrade badge for outdated applications and invokes this callback with the
 *   matching application.
 * @returns A new list of column definitions with template output columns grouped under their respective templates
 */
export interface EditingTemplateApplicationIds {
  modelContainerId?: string;
}

/** First table application for the template container being edited (version may differ while editing a draft). */
export const findEditingApplicationIndexForPreview = (
  applications: TemplateTableApplicationsOutputV1[],
  editingTemplate?: EditingTemplateApplicationIds,
): number => {
  const containerId = normalizeId(editingTemplate?.modelContainerId);
  if (!containerId) {
    return -1;
  }

  return applications.findIndex((app) => normalizeId(app.templateContainerId) === containerId);
};

export function getTemplateColumnsDefs(
  agColumnDefs: ColDef[],
  applications: TemplateTableApplicationsOutputV1[],
  inputFeatureColumnIds: string[],
  versionByModelGuid?: Map<string, ApplicationVersionInfo>,
  editingTemplate?: EditingTemplateApplicationIds,
  editingTemplatePreviewColumnIds?: string[],
  onUpgradeApplication?: (application: TemplateTableApplicationsOutputV1) => void,
): (ColDef | ColGroupDef)[] {
  const editingApplicationIndex = findEditingApplicationIndexForPreview(applications, editingTemplate);

  const groups: ColGroupDef[] = applications.map((app, applicationIndex) => {
    const children = agColumnDefs.filter((colDef) => {
      const field = colDef.field as string;
      if (inputFeatureColumnIds.includes(field)) {
        return false;
      }
      const inMappings = app.mappings?.some((m) => m.columnId === field);
      const inEditPreview =
        applicationIndex === editingApplicationIndex &&
        editingApplicationIndex !== -1 &&
        editingTemplatePreviewColumnIds?.includes(field);
      return inMappings || inEditPreview;
    });

    const versionInfo = app.templateGuid ? versionByModelGuid?.get(app.templateGuid) : undefined;
    const onUpgrade = onUpgradeApplication ? () => onUpgradeApplication(app) : undefined;

    return {
      headerName: app.templateName,
      marryChildren: true,
      children,
      ...(versionByModelGuid && {
        headerGroupComponent: ModelColumnHeader,
        headerGroupComponentParams: { versionInfo, onUpgrade },
      }),
    } as ColGroupDef;
  });

  const addedGroups = new Map<any, true>();
  const allColumns: (ColDef | ColGroupDef)[] = [];

  // Find the group that contains a given column field
  const findGroupForField = (field?: string) =>
    field ? groups.find((g) => (g.children as ColDef[]).some((child) => child.field === field)) : undefined;

  // Iterate through the original column definitions and add them to the new list, grouping if the column belongs to a template
  // and hasn't already been added
  agColumnDefs.forEach((colDef) => {
    const group = findGroupForField(colDef.field as string);

    if (group) {
      if (!addedGroups.has(group)) {
        addedGroups.set(group, true);
        allColumns.push(group);
      }
    } else {
      allColumns.push(colDef);
    }
  });

  return allColumns;
}

/**
 * Duplicates a given template by creating a new name, scoping it to the current workbook, and then
 * returning the result.
 *
 * @param originalTemplate - The template to duplicate (only id and name are needed)
 * @param workbookId - The ID of the workbook to scope the duplicate to
 * @returns The newly created template
 */
export const duplicateTemplate = async (
  originalTemplate: { id: string; name: string },
  workbookId: string,
): Promise<TemplateOutputV1> => {
  const duplicateName = t('TEMPLATES.DUPLICATED_TEMPLATE_NAME', { originalTemplateName: originalTemplate.name });
  const duplicateResult = await sqTemplatesApi.duplicateTemplate(
    { name: duplicateName, scopedTo: workbookId },
    { templateId: originalTemplate.id },
  );
  return duplicateResult.data;
};

/**
 * Configuration for creating input mappings for template applications.
 */
export interface CreateInputMappingsConfig {
  /** The input features to map */
  inputFeatures: TemplateInput[];
  /** Set of input feature IDs that are exclusively used as variable parameters */
  variableOnlyInputIds: Set<string>;
  /** Function to check if an input is mapped (e.g., has an inputItem or columnDefinition) */
  isMapped: (input: TemplateInput) => boolean;
  /** Instance name to use, or undefined to auto-generate with timestamp */
  instanceName?: string;
  /** Function to extract the input mapping value (itemIdInput for tool, columnId for table) */
  getInputMapping: (input: TemplateInput) => { itemIdInput?: string; columnId?: string };
}

/**
 * Creates input mappings for template applications, handling both tool and table contexts.
 * Filters out unmapped variable-only inputs and handles instance name generation.
 *
 * @param config - Configuration for creating input mappings
 * @returns Array of template application mappings
 */
export const createInputMappings = (config: CreateInputMappingsConfig): TemplateApplicationMappingV1[] => {
  const { inputFeatures, variableOnlyInputIds, isMapped, instanceName, getInputMapping } = config;

  return inputFeatures
    .filter((input) => {
      if (input.name === SeeqNames.Models.InstanceNameInputFeatureName) return true;
      // Exclude unmapped variable-only inputs
      return !(input.id && variableOnlyInputIds.has(input.id) && !isMapped(input));
    })
    .map((input) => {
      // CRAB-53616: The auto-generated instance name should be replaced with user input in the future
      const isInstanceName = input.name === SeeqNames.Models.InstanceNameInputFeatureName;

      let textInput: string | undefined;
      if (isInstanceName) {
        if (instanceName !== undefined && instanceName !== '') {
          textInput = instanceName;
        } else if (input.inputText) {
          textInput = input.inputText;
        } else {
          textInput = `Instance Name - ${new Date().toISOString()}`;
        }
      }

      return {
        featureId: input.id,
        textInput,
        ...(isInstanceName ? {} : getInputMapping(input)),
      };
    });
};

export const getOutputMapping = (
  existingDerivedFeatures: TemplateDerivedFeature[],
): TemplateDerivedColumnNamesInputV1[] =>
  existingDerivedFeatures.map((feature) => ({
    columnName: feature.name,
    derivedFeatureId: feature.id!,
  }));

/**
 * Returns a copy of the Derived Feature with `newName` applied to its top-level name AND to each
 * Column Rule's `name` argument across the possible rules creators
 */
export function updateFeatureName<T extends { name?: string; rules?: FeatureRuleV1[] }>(
  feature: T,
  newName: string,
): T {
  const renameRule = <R extends { name?: TemplateRuleInputV1String }>(rule: R | undefined): R | undefined =>
    rule?.name?.type === TemplateRuleInputV1StringTypeEnum.TEXT
      ? { ...rule, name: { ...rule.name, argument: newName } }
      : rule;
  return {
    ...feature,
    name: newName,
    rules: feature.rules?.map((rule) => ({
      ...rule,
      formulaCreator: renameRule(rule.formulaCreator),
      numericScalarCreator: renameRule(rule.numericScalarCreator),
      stringScalarCreator: renameRule(rule.stringScalarCreator),
    })),
  };
}

export const exposedForTesting = {
  validateApplication,
  applyTemplateOnce,
  reapplyTemplate,
  addTemplateItemsToTrend,
  retireTableInstance,
  createInputMappings,
};
